import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Form, Button, Modal } from "react-bootstrap";
import "../../styles/style.scss";
import "../../styles/WeeklyPlan.scss";
import Select from "react-select";
import { calculateDayTotals, formatDayTotals, calculateMealServings } from "../../utils/weeklyPlanCalculations";
import { weeklyPlanAPI } from "../../services/weeklyPlanAPI";
import WeeklyPlanDisplay from "./components/WeeklyPlanDisplay";
import AddRecipeForm from "./components/AddRecipeForm";
import AddMealForm from "./components/AddMealForm";
import EditRecipeModal from "./components/EditRecipeModal";
import ViewMealModal from "./components/ViewMealModal";
import { WeeklyPlanToastContainer, showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from "./components/ToastContainer";
import { WeeklyPlanSkeleton, LoadingButton } from "./components/LoadingSkeleton";
import { DeleteConfirmationModal, TransitionConfirmationModal } from "./components/ConfirmationModal";
import { ClearDayConfirmationModal, ClearMealConfirmationModal, ClearAllDaysConfirmationModal } from "./components/BulkOperationModal";
import SuccessCheck from "./components/SuccessCheck";
import { useAnimations } from "./hooks/useAnimations";
import CopyDayModal from "./components/CopyDayModal";
import WeeklyPrepListModal from "./components/WeeklyPrepListModal";
import ThawReminderAlert from "./components/ThawReminderAlert";

function WeeklyPlan() {
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [userSettings, setUserSettings] = useState(null);
  const [nextWeekPlanningEnabled, setNextWeekPlanningEnabled] = useState(false);
  const [currentPlanType, setCurrentPlanType] = useState('current');
  const [planSummary, setPlanSummary] = useState({ 
    currentWeek: { recipeCount: 0 }, 
    nextWeek: { recipeCount: 0 } 
  });
  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const data = await weeklyPlanAPI.checkAuth();
        if (data.isLoggedIn) {
          setIsLoggedIn(true);
          // Fetch user settings to check feature flag
          try {
            const settingsData = await weeklyPlanAPI.getUserSettings();
            setUserSettings(settingsData.user);
            setNextWeekPlanningEnabled(settingsData.user.next_week_planning_enabled || false);
          } catch (error) {
            console.error("Error fetching user settings:", error);
          }
        } else {
          throw new Error("User is not logged in");
        }
      } catch (error) {
        console.error("Error checking if user is logged in:", error);
        setIsLoggedIn(false);
      }
    };
    checkAuth();
  }, []);

  const [recipes, setRecipes] = useState([]);
  const [meals, setMeals] = useState([]);
  const [onlyUserRecipes, setOnlyUserRecipes] = useState(false);
  const [multiplier, setMultiplier] = useState(1);
  const [mealMultiplier, setMealMultiplier] = useState(1);

  // Consolidated meal selection state
  const [selectedMealData, setSelectedMealData] = useState({
    meal: [],
    label: "",
    value: "",
    servings: "",
    dayValue: "",
    dayName: "",
    courseValue: "",
    courseName: ""
  });

  // Consolidated recipe selection state
  const [selectedRecipeData, setSelectedRecipeData] = useState({
    recipe: [],
    label: "",
    value: "",
    servings: 1, // Initialize with 1 instead of empty string
    dayValue: "",
    dayName: "",
    courseValue: "",
    courseName: ""
  });

  const [showEditModal, setShowEditModal] = useState(false);
  const [editMultiplier, setEditMultiplier] = useState(1);

  // Consolidated edit recipe state
  const [editRecipeData, setEditRecipeData] = useState({
    recipeId: "",
    recipe: [],
    label: "",
    value: "",
    servings: "",
    dayValue: "",
    dayName: "",
    courseValue: "",
    courseName: ""
  });

  const [weeklyPlan, setWeeklyPlan] = useState([]);
  const [recipeIngredients, setRecipeIngredients] = useState([]);

  const [isFree, setIsFree] = useState(true);

  // Memoize day totals calculation
  const dayTotals = useMemo(() => {
    const totals = calculateDayTotals(weeklyPlan);
    return formatDayTotals(totals);
  }, [weeklyPlan]);

  const filteredRecipesWithoutFree = useMemo(() => 
    recipes.filter(
      (recipe) => !onlyUserRecipes || recipe.user_id !== 1
    ), [recipes, onlyUserRecipes]
  );

  const [onlyUserMeals, setOnlyUserMeals] = useState(false);
  const [showViewMealModal, setShowViewMealModal] = useState(false);
  const [viewMeal, setViewMeal] = useState([]);
  const [viewMealId, setViewMealId] = useState("");
  const [viewMealRecipe, setViewMealRecipe] = useState([]);

  const [onlyDayValue, setOnlyDayValue] = useState("");
  const [onlyDayName, setOnlyDayName] = useState("");

  const [onlyCourseValue, setOnlyCourseValue] = useState("");
  const [onlyCourseName, setOnlyCourseName] = useState("");

  const [showShoppingListConfirm, setShowShoppingListConfirm] = useState(true);
  const [showEditShoppingListConfirm, setShowEditShoppingListConfirm] =
    useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAddingRecipe, setIsAddingRecipe] = useState(false);
  const [isAddingMeal, setIsAddingMeal] = useState(false);
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Confirmation modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showTransitionConfirm, setShowTransitionConfirm] = useState(false);
  
  // Animation hooks
  const { addAnimation, showSuccessAnimation, getAnimationClass, showSuccess } = useAnimations();
  
  // Bulk operation states
  const [clearDayTarget, setClearDayTarget] = useState(null);
  const [showClearDayConfirm, setShowClearDayConfirm] = useState(false);
  const [isClearingDay, setIsClearingDay] = useState(false);
  const [clearMealTarget, setClearMealTarget] = useState({ day: null, meal: null });
  const [showClearMealConfirm, setShowClearMealConfirm] = useState(false);
  const [isClearingMeal, setIsClearingMeal] = useState(false);
  const [showClearAllDaysConfirm, setShowClearAllDaysConfirm] = useState(false);
  const [isClearingAllDays, setIsClearingAllDays] = useState(false);
  const [copyDaySource, setCopyDaySource] = useState(null);
  const [showCopyDayModal, setShowCopyDayModal] = useState(false);
  const [isCopyingDay, setIsCopyingDay] = useState(false);
  
  // Prep list modal states
  const [showPrepListModal, setShowPrepListModal] = useState(false);
  const [prepItems, setPrepItems] = useState({});
  const [hasPrepItems, setHasPrepItems] = useState(false);
  const [isMetric, setIsMetric] = useState(false);

  // Thaw reminder states
  const [thawReminders, setThawReminders] = useState([]);
  const [thawWeekStartDate, setThawWeekStartDate] = useState(null);

  const handleAddToShoppingList = async (id) => {
    console.log("Adding to shopping list for recipe ID:", id);
    try {
      const data = await weeklyPlanAPI.fetchRecipeIngredients(id);
      console.log("Ingredients data:", data);

      // Filter out section headers and text rows - these are for organization only
      const actualIngredients = data.filter(ingredient =>
        !ingredient.is_section_header && !ingredient.is_text_row
      );

      // Prepare all shopping items
      const shoppingItemPromises = actualIngredients.map(ingredient => {
        const ingredientId = ingredient.old_id.split(",")[0];
        const customIngredientFlag = ingredient.old_id.includes("user") ? 1 : 0;
        
        return weeklyPlanAPI.addShoppingItem({
          ingredient_id: ingredientId,
          custom_ingredient: customIngredientFlag,
          qty: (multiplier === "" || isNaN(multiplier) ? 1 : parseFloat(multiplier)) * ingredient.qty * ingredient.conversion_factor,
          day: selectedRecipeData.dayValue,
          meal: selectedRecipeData.courseValue,
          for_recipe: id, // Use the recipe ID directly, not the recipe array
          original_id_string: ingredient.old_id,
        }).catch(err => {
          console.error("Error adding ingredient to shopping list:", err);
        });
      });
      
      // Batch add all items
      await Promise.all(shoppingItemPromises);
      
      await fetchWeeklyPlan();
    } catch (error) {
      console.error("Error fetching recipe ingredients:", error);
    }
  };

  // Helper function to add shopping list items with specific day/meal
  const addToShoppingListWithDayMeal = async (recipeId, day, meal, mealQty = 1) => {
    console.log(`Adding to shopping list: recipe=${recipeId}, day=${day}, meal=${meal}, qty=${mealQty}`);
    try {
      const data = await weeklyPlanAPI.fetchRecipeIngredients(recipeId);

      // Filter out section headers and text rows - these are for organization only
      const actualIngredients = data.filter(ingredient =>
        !ingredient.is_section_header && !ingredient.is_text_row
      );

      const shoppingItemPromises = actualIngredients.map(ingredient => {
        const ingredientId = ingredient.old_id.split(",")[0];
        const customIngredientFlag = ingredient.old_id.includes("user") ? 1 : 0;
        
        return weeklyPlanAPI.addShoppingItem({
          ingredient_id: ingredientId,
          custom_ingredient: customIngredientFlag,
          qty: mealQty * ingredient.qty * ingredient.conversion_factor,
          day: day,
          meal: meal,
          for_recipe: recipeId,
          original_id_string: ingredient.old_id,
        }).catch(err => {
          console.error("Error adding ingredient to shopping list:", err);
        });
      });
      
      await Promise.all(shoppingItemPromises);
    } catch (error) {
      console.error("Error fetching recipe ingredients:", error);
    }
  };

  const handleEditRecipe = (recipe) => {
    setEditRecipeData({
      recipeId: recipe.id,
      recipe: recipe.recipe_id,
      label: recipe.name,
      value: recipe.recipe_id,
      servings: recipe.servings,
      dayValue: recipe.day,
      dayName: recipe.day,
      courseValue: recipe.meal,
      courseName: recipe.meal,
      originalRecipe: recipe // Store the full original recipe for delete operation
    });
    setEditMultiplier(recipe.meal_qty || 1); // Fixed typo: meal_mutliplyer -> meal_qty
    setShowEditShoppingListConfirm(true); // Default to checked
    setShowEditModal(true);
  };

  const handleViewMeal = async (meal) => {
    console.log(meal);
    setViewMeal(meal);
    setViewMealId(meal.meal_id);
    
    try {
      const data = await weeklyPlanAPI.fetchMealRecipes(meal.meal_id);
      console.log(data);
      setViewMealRecipe(data);
      setShowViewMealModal(true);
    } catch (err) {
      console.error("Error retrieving meal:", err);
    }
  };

  const handleShoppingListAddMeal = async (mealId) => {
    console.log("Adding meal ingredients to shopping list:", mealId);
    
    try {
      const mealRecipes = await weeklyPlanAPI.fetchMealRecipes(mealId);

      // Batch fetch all ingredients for all recipes
      const allIngredientsPromises = mealRecipes.map(recipeData => 
        weeklyPlanAPI.fetchRecipeIngredients(recipeData.id)
          .then(ingredients => ({ recipeData, ingredients }))
      );
      
      const allRecipeIngredients = await Promise.all(allIngredientsPromises);
      
      // Prepare all shopping items to add
      const shoppingItemPromises = [];
      
      for (const { recipeData, ingredients } of allRecipeIngredients) {
        const recipeId = recipeData.id;
        const meal_specific_multi = recipeData.meal_mutliplyer; // Backend field name has typo

        // Filter out section headers and text rows - these are for organization only
        const actualIngredients = ingredients.filter(ingredient =>
          !ingredient.is_section_header && !ingredient.is_text_row
        );

        for (const ingredient of actualIngredients) {
          const ingredientId = ingredient.old_id.split(",")[0];
          const customIngredientFlag = ingredient.old_id.includes("user") ? 1 : 0;

          console.log("Adding ingredient to shopping list:", ingredient);

          shoppingItemPromises.push(
            weeklyPlanAPI.addShoppingItem({
              ingredient_id: ingredientId,
              custom_ingredient: customIngredientFlag,
              qty:
                (mealMultiplier === "" || isNaN(mealMultiplier) ? 1 : parseFloat(mealMultiplier)) *
                meal_specific_multi *
                ingredient.qty *
                ingredient.conversion_factor,
              day: selectedMealData.dayValue,
              meal: selectedMealData.courseValue,
              for_recipe: recipeId,
              original_id_string: ingredient.old_id,
            })
          );
        }
      }
      
      // Batch add all shopping items
      await Promise.all(shoppingItemPromises);
    } catch (error) {
      console.error("Error handling shopping list for meal:", error);
    }
  };

  const handleEditShoppingListAdd = async (id) => {
    try {
      const data = await weeklyPlanAPI.fetchRecipeIngredients(id);
      console.log("Edit shopping list - ingredients data:", data);

      // Filter out section headers and text rows - these are for organization only
      const actualIngredients = data.filter(ingredient =>
        !ingredient.is_section_header && !ingredient.is_text_row
      );

      // Prepare all shopping items
      const shoppingItemPromises = actualIngredients.map(ingredient => {
        const ingredientId = ingredient.old_id.split(",")[0];
        const customIngredientFlag = ingredient.old_id.includes("user") ? 1 : 0;
        
        return weeklyPlanAPI.addShoppingItem({
          ingredient_id: ingredientId,
          custom_ingredient: customIngredientFlag,
          qty: (editMultiplier === "" || isNaN(editMultiplier) ? 1 : parseFloat(editMultiplier)) * ingredient.qty * ingredient.conversion_factor,
          day: editRecipeData.dayValue,
          meal: editRecipeData.courseValue,
          for_recipe: id, // Use the recipe ID directly
          original_id_string: ingredient.old_id,
        }).catch(err => {
          console.error("Error adding ingredient to shopping list:", err);
        });
      });
      
      // Batch add all items
      await Promise.all(shoppingItemPromises);
      
      await fetchWeeklyPlan();
    } catch (error) {
      console.error("Error fetching recipe ingredients:", error);
    }
  };

  const dayOptions = [
    { value: "None", label: "None" },
    { value: "Sunday", label: "Sunday" },
    { value: "Monday", label: "Monday" },
    { value: "Tuesday", label: "Tuesday" },
    { value: "Wednesday", label: "Wednesday" },
    { value: "Thursday", label: "Thursday" },
    { value: "Friday", label: "Friday" },
    { value: "Saturday", label: "Saturday" },
  ];

  const courseOptions = [
    { value: "Appetizer", label: "Appetizer" },
    { value: "Breakfast", label: "Breakfast" },
    { value: "Lunch", label: "Lunch" },
    { value: "Dinner", label: "Dinner" },
    { value: "Dessert", label: "Dessert" },
    { value: "Snack", label: "Snack" },
    { value: "Other", label: "Other" },
  ];

  const handleViewRecipe = useCallback((recipeId) => {
    window.location.href = `/view-recipe/${recipeId}`;
  }, []);

  const handleDeleteFromDay = useCallback((recipe) => {
    setDeleteTarget(recipe);
    setShowDeleteConfirm(true);
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    
    setIsDeleting(true);
    
    // Add removing animation
    addAnimation(`recipe-${deleteTarget.id}`, 'removing', 300);
    
    // Wait for animation to start
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      console.log("Deleting recipe:", deleteTarget);
      const response = await weeklyPlanAPI.removeRecipeFromPlan({
        rowId: deleteTarget.id,
        recipeId: deleteTarget.recipe_id,
        recipeDay: deleteTarget.day,
        recipeMeal: deleteTarget.meal,
      });
      
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      
      // Fetch updated weekly plan with correct plan type
      const data = await weeklyPlanAPI.fetchPlan(currentPlanType);
      setWeeklyPlan(data);
      
      // Update plan summary if next week planning is enabled
      if (nextWeekPlanningEnabled) {
        const summary = await weeklyPlanAPI.fetchPlanSummary();
        setPlanSummary(summary);
      }
      
      showSuccessToast(`Removed "${deleteTarget.name}" from ${deleteTarget.day} ${deleteTarget.meal}`);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error("Error deleting recipe from weekly plan:", error);
      showErrorToast("Failed to remove recipe. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const updateWeeklyPlanRecipe = async () => {
    if (isUpdating) {
      console.log("Update already in progress, ignoring duplicate request");
      return;
    }
    
    setIsUpdating(true);
    
    try {
      // Check what's changing
      const isDayOrMealChanging = 
        editRecipeData.originalRecipe.day !== editRecipeData.dayValue || 
        editRecipeData.originalRecipe.meal !== editRecipeData.courseValue;
      
      const isRecipeChanging = 
        editRecipeData.originalRecipe.recipe_id !== editRecipeData.value;
      
      const isQuantityChanging = 
        editRecipeData.originalRecipe.meal_qty !== (editMultiplier === "" || isNaN(editMultiplier) ? 1 : parseFloat(editMultiplier));
      
      // For next week items, shopping list should NEVER be touched
      const isEditingNextWeek = currentPlanType === 'next';
      
      if (isEditingNextWeek) {
        // For next week, just delete and re-add without any shopping list logic
        const deleteResponse = await weeklyPlanAPI.deleteWeeklyPlanOnly(editRecipeData.originalRecipe.id);
        
        if (!deleteResponse.ok) {
          throw new Error("Error deleting weekly plan entry");
        }
      } else if (showEditShoppingListConfirm) {
        // Current week with checkbox checked - use normal delete/add flow (which updates shopping list)
        const response = await weeklyPlanAPI.removeRecipeFromPlan({
          rowId: editRecipeData.originalRecipe.id,
          recipeId: editRecipeData.originalRecipe.recipe_id,
          recipeDay: editRecipeData.originalRecipe.day,
          recipeMeal: editRecipeData.originalRecipe.meal,
        });
        
        if (!response.ok) {
          throw new Error("Error removing old recipe entry");
        }
      } else {
        // Current week with checkbox unchecked
        if (isRecipeChanging) {
          const proceed = window.confirm(
            "You're changing to a different recipe. The shopping list must be refreshed with the new recipe's ingredients. " +
            "Do you want to continue?"
          );
          
          if (!proceed) {
            return;
          }
          
          // Use normal delete flow to remove old recipe's ingredients
          const response = await weeklyPlanAPI.removeRecipeFromPlan({
            rowId: editRecipeData.originalRecipe.id,
            recipeId: editRecipeData.originalRecipe.recipe_id,
            recipeDay: editRecipeData.originalRecipe.day,
            recipeMeal: editRecipeData.originalRecipe.meal,
          });
          
          if (!response.ok) {
            throw new Error("Error removing old recipe entry");
          }
        } else {
          // Recipe is the same - handle based on what changed
          if (isQuantityChanging && showEditShoppingListConfirm) {
            // If quantity changed and user wants shopping list updated, use full delete/add flow
            const response = await weeklyPlanAPI.removeRecipeFromPlan({
              rowId: editRecipeData.originalRecipe.id,
              recipeId: editRecipeData.originalRecipe.recipe_id,
              recipeDay: editRecipeData.originalRecipe.day,
              recipeMeal: editRecipeData.originalRecipe.meal,
            });
            
            if (!response.ok) {
              throw new Error("Error removing old recipe entry for quantity update");
            }
          } else {
            // Just delete weekly plan entry without affecting shopping list
            const deleteResponse = await weeklyPlanAPI.deleteWeeklyPlanOnly(editRecipeData.originalRecipe.id);
            
            if (!deleteResponse.ok) {
              throw new Error("Error deleting weekly plan entry");
            }
            
            // If only day or meal changed, update the shopping list items (current week only)
            if (isDayOrMealChanging) {
              const updateResponse = await weeklyPlanAPI.updateShoppingListDayMeal({
                recipeId: editRecipeData.originalRecipe.recipe_id,
                oldDay: editRecipeData.originalRecipe.day,
                oldMeal: editRecipeData.originalRecipe.meal,
                newDay: editRecipeData.dayValue,
                newMeal: editRecipeData.courseValue
              });
              
              if (!updateResponse.ok) {
                console.error("Failed to update shopping list items");
              }
            }
          }
        }
      }
      
      // Then add the updated entry with the correct plan_type
      const recipeData = {
        recipe_id: editRecipeData.value,
        meal_qty: editMultiplier === "" || isNaN(editMultiplier) ? 1 : parseFloat(editMultiplier),
        day: editRecipeData.dayValue,
        meal: editRecipeData.courseValue,
      };

      const addResponse = await weeklyPlanAPI.addRecipe(recipeData, currentPlanType);
      if (!addResponse.ok) {
        throw new Error("Error adding updated weekly plan recipe");
      }
      
      // Only add to shopping list if:
      // 1. We're editing a current week item AND
      // 2. Either: checkbox is checked AND (recipe changed OR day/meal changed OR quantity changed) OR recipe changed (must refresh)
      if (currentPlanType === 'current' && (isRecipeChanging || (showEditShoppingListConfirm && (isDayOrMealChanging || isQuantityChanging)))) {
        await handleEditShoppingListAdd(editRecipeData.value);
      }
      
      setShowEditModal(false);
      
      // Fetch updated weekly plan with correct plan type
      const data = await weeklyPlanAPI.fetchPlan(currentPlanType);
      setWeeklyPlan(data);
      
      // Update plan summary if next week planning is enabled
      if (nextWeekPlanningEnabled) {
        const summary = await weeklyPlanAPI.fetchPlanSummary();
        setPlanSummary(summary);
      }
      
      showSuccessToast("Recipe updated successfully!");
    } catch (error) {
      console.error("Error updating weekly plan recipe:", error);
      showErrorToast("Failed to update recipe. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddRecipe = async () => {
    if (!selectedRecipeData.value || !selectedRecipeData.dayValue || !selectedRecipeData.courseValue) {
      showWarningToast("Please select a recipe, day, and course before adding.");
      return;
    }

    if (isAddingRecipe) {
      return; // Prevent double-clicking
    }

    setIsAddingRecipe(true);

    const recipeData = {
      recipe_id: selectedRecipeData.value,
      servings: selectedRecipeData.servings,
      day: selectedRecipeData.dayValue,
      meal: selectedRecipeData.courseValue,
      meal_qty: multiplier === "" || isNaN(multiplier) ? 1 : parseFloat(multiplier), // Ensure valid number
    };

    try {
      const response = await weeklyPlanAPI.addRecipe(recipeData, currentPlanType);
      if (!response.ok) {
        if (response.status === 403) {
          showWarningToast(
            "You've reached the maximum number of recipe instances allowed for your membership level. Please upgrade your membership to add more."
          );
        } else {
          throw new Error("Error adding recipe to weekly plan");
        }
        return;
      }
      
      // The backend returns "OK" as plain text, not JSON
      const responseText = await response.text();
      
      // Only add to shopping list for current week items
      if (showShoppingListConfirm && currentPlanType === 'current') {
        await handleAddToShoppingList(selectedRecipeData.value);
      }
      
      await fetchWeeklyPlan();
      
      // Update plan summary if next week planning is enabled
      if (nextWeekPlanningEnabled) {
        const summary = await weeklyPlanAPI.fetchPlanSummary();
        setPlanSummary(summary);
      }
      
      setSelectedRecipeData({
        recipe: [],
        label: "",
        value: "",
        servings: 1, // Reset to 1 instead of empty string
        dayValue: selectedRecipeData.dayValue,
        dayName: selectedRecipeData.dayName,
        courseValue: selectedRecipeData.courseValue,
        courseName: selectedRecipeData.courseName
      });
      setMultiplier(1);
      
      showSuccessToast(`Added "${selectedRecipeData.label}" to ${selectedRecipeData.dayName} ${selectedRecipeData.courseName}`);
      showSuccessAnimation();
    } catch (error) {
      console.error("Error adding recipe to weekly plan:", error);
      showErrorToast("Failed to add recipe. Please try again.");
    } finally {
      setIsAddingRecipe(false);
    }
  };

  const handleAddMeal = async () => {
    if (!selectedMealData.value || !selectedMealData.dayValue || !selectedMealData.courseValue) {
      showWarningToast("Please select a meal, day, and course before adding.");
      return;
    }

    if (isAddingMeal) {
      return; // Prevent double-clicking
    }

    setIsAddingMeal(true);

    try {
      // First, fetch the recipes that make up this meal
      const mealRecipes = await weeklyPlanAPI.fetchMealRecipes(selectedMealData.value);
      
      if (!mealRecipes || mealRecipes.length === 0) {
        showWarningToast("This meal has no recipes associated with it.");
        return;
      }

      // Add each recipe in the meal to the weekly plan
      await Promise.all(
        mealRecipes.map(async (recipe) => {
          const validMealMultiplier = mealMultiplier === "" || isNaN(mealMultiplier) ? 1 : parseFloat(mealMultiplier);
          const mealQty = validMealMultiplier * recipe.meal_mutliplyer;
          
          const recipeData = {
            recipe_id: recipe.id,
            meal_qty: mealQty,
            day: selectedMealData.dayValue,
            meal: selectedMealData.courseValue,
          };

          const response = await weeklyPlanAPI.addRecipe(recipeData, currentPlanType);
          if (!response.ok) {
            if (response.status === 403) {
              throw new Error("membership_limit");
            } else {
              throw new Error("Error adding recipe to weekly plan");
            }
          }
        })
      );
      
      // Only add to shopping list for current week items
      if (showShoppingListConfirm && currentPlanType === 'current') {
        await handleShoppingListAddMeal(selectedMealData.value);
      }
      
      await fetchWeeklyPlan();
      
      // Update plan summary if next week planning is enabled
      if (nextWeekPlanningEnabled) {
        const summary = await weeklyPlanAPI.fetchPlanSummary();
        setPlanSummary(summary);
      }
      
      setSelectedMealData({
        meal: [],
        label: "",
        value: "",
        servings: "",
        dayValue: selectedMealData.dayValue,
        dayName: selectedMealData.dayName,
        courseValue: selectedMealData.courseValue,
        courseName: selectedMealData.courseName
      });
      setMealMultiplier(1);
      
      showSuccessToast(`Added meal "${selectedMealData.label}" to ${selectedMealData.dayName} ${selectedMealData.courseName}`);
      showSuccessAnimation();
    } catch (error) {
      if (error.message === "membership_limit") {
        showWarningToast(
          "You've reached the maximum number of recipe instances allowed for your membership level. Please upgrade your membership to add more."
        );
      } else {
        console.error("Error adding meal to weekly plan:", error);
        showErrorToast("Failed to add meal. Please try again.");
      }
    } finally {
      setIsAddingMeal(false);
    }
  };

  const resetDayServings = () => {
    setMultiplier(1);
    if (selectedRecipeData.recipe.length > 0 && selectedRecipeData.recipe[0].servings) {
      setSelectedRecipeData(prev => ({
        ...prev,
        servings: selectedRecipeData.recipe[0].servings
      }));
    }
  };

  const resetMealDayServings = () => {
    setMealMultiplier(1);
    if (selectedMealData.meal.length > 0 && selectedMealData.meal[0].servings) {
      setSelectedMealData(prev => ({
        ...prev,
        servings: selectedMealData.meal[0].servings
      }));
    } else {
      setSelectedMealData(prev => ({
        ...prev,
        servings: ""
      }));
      setMealMultiplier("");
    }
  };

  const fetchWeeklyPlan = async () => {
    setIsLoadingPlan(true);
    try {
      const data = await weeklyPlanAPI.fetchPlan(currentPlanType);
      setWeeklyPlan(data);

      console.log(data);

      // Fetch prep items if current week
      if (currentPlanType === 'current') {
        fetchPrepItems();
      }

      // Fetch thaw reminders
      fetchThawReminders(currentPlanType);
    } catch (err) {
      console.error("Error retrieving weekly plan:", err);
      showErrorToast("Failed to load weekly plan. Please refresh the page.");
    } finally {
      setIsLoadingPlan(false);
    }
  };
  
  const fetchPrepItems = async () => {
    try {
      const data = await weeklyPlanAPI.fetchWeeklyPrepItems();
      console.log("Prep items fetched:", data);
      setPrepItems(data);
      // Check if there are any prep items
      const hasItems = Object.keys(data).some(day => data[day] && data[day].length > 0);
      setHasPrepItems(hasItems);
      console.log("Has prep items:", hasItems);
    } catch (err) {
      console.error("Error fetching prep items:", err);
      // Don't show error toast for prep items as it's not critical
    }
  };

  // Fetch thaw reminders from the API
  const fetchThawReminders = async (planType) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/weekly-plan/thaw-reminders?planType=${planType}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setThawReminders(data.reminders || []);
        setThawWeekStartDate(data.weekStartDate);
      }
    } catch (err) {
      console.error("Error fetching thaw reminders:", err);
    }
  };

  // Dismiss a single thaw reminder
  const handleDismissThawReminder = async (reminder) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/weekly-plan/thaw-reminders/dismiss`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ingredientId: reminder.ingredient_id,
            ingredientType: reminder.ingredient_type,
            planType: currentPlanType,
            weekStartDate: thawWeekStartDate,
            scheduledDay: reminder.day_of_week
          })
        }
      );
      if (response.ok) {
        // Remove from local state
        setThawReminders(prev => prev.filter(r =>
          !(r.ingredient_id === reminder.ingredient_id &&
            r.ingredient_type === reminder.ingredient_type &&
            r.day_of_week === reminder.day_of_week)
        ));
      }
    } catch (err) {
      console.error("Error dismissing thaw reminder:", err);
      showErrorToast("Failed to dismiss reminder");
    }
  };

  // Dismiss all thaw reminders
  const handleDismissAllThawReminders = async () => {
    try {
      // Dismiss each reminder
      await Promise.all(thawReminders.map(reminder =>
        fetch(`${process.env.REACT_APP_API_URL}/api/weekly-plan/thaw-reminders/dismiss`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ingredientId: reminder.ingredient_id,
            ingredientType: reminder.ingredient_type,
            planType: currentPlanType,
            weekStartDate: thawWeekStartDate,
            scheduledDay: reminder.day_of_week
          })
        })
      ));
      setThawReminders([]);
    } catch (err) {
      console.error("Error dismissing all thaw reminders:", err);
      showErrorToast("Failed to dismiss reminders");
    }
  };

  const fetchUserSetting = async () => {
    try {
      const { user } = await weeklyPlanAPI.fetchUserSettings();

      if (!user) {
        // Handle error
      }
      console.log(user);
      setOnlyUserRecipes(user.only_user_recipes === 1);
      setIsFree(user.membership_level === "free");
      setIsMetric(user.default_unit_of_measurement === "metric");
    } catch (error) {
      console.error("Error fetching user settings:", error);
    }
  };

  const handleToggleUserRecipes = async () => {
    setOnlyUserRecipes(!onlyUserRecipes);
    try {
      await weeklyPlanAPI.updateUserSettings('only_user_recipes', onlyUserRecipes ? 0 : 1);
    } catch (error) {
      console.error("Error updating user settings:", error);
    }
  };

  const handleToggleUserMeals = () => {
    setOnlyUserMeals(!onlyUserMeals);
  };

  // Fetch Recipes List
  useEffect(() => {
    const fetchRecipes = async () => {
      try {
        const data = await weeklyPlanAPI.fetchAllRecipes();
        console.log(data);
        const removedHiddenRecipes = data.filter(
          (recipe) => recipe.hidden === 0
        );
        setRecipes(removedHiddenRecipes);
      } catch (err) {
        console.error(err);
      }
    };
    fetchRecipes();
  }, []);

  // Fetch Meal List
  useEffect(() => {
    const fetchMeals = async () => {
      try {
        const data = await weeklyPlanAPI.fetchUserMeals();
        console.log("meals data:", data);
        setMeals(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchMeals();
  }, []);

  // Fetch weekly plan on mount
  useEffect(() => {
    const initializeWeeklyPlan = async () => {
      if (isLoggedIn) {
        await fetchWeeklyPlan();
        fetchUserSetting();
        // Set initial values for day and course
        setSelectedRecipeData(prev => ({
          ...prev,
          dayValue: "None",
          dayName: "None",
          courseValue: "Dinner",
          courseName: "Dinner"
        }));
        setSelectedMealData(prev => ({
          ...prev,
          dayValue: "None", 
          dayName: "None",
          courseValue: "Dinner",
          courseName: "Dinner"
        }));
      }
    };
    
    initializeWeeklyPlan();
  }, [isLoggedIn]);

  // Fetch plan summary when feature is enabled
  useEffect(() => {
    if (isLoggedIn && nextWeekPlanningEnabled) {
      const fetchSummary = async () => {
        try {
          const summary = await weeklyPlanAPI.fetchPlanSummary();
          setPlanSummary(summary);
        } catch (error) {
          console.error("Error fetching plan summary:", error);
        }
      };
      fetchSummary();
    }
  }, [isLoggedIn, nextWeekPlanningEnabled]);

  // Refetch weekly plan when plan type changes
  useEffect(() => {
    const refetchPlan = async () => {
      if (isLoggedIn && nextWeekPlanningEnabled) {
        await fetchWeeklyPlan();
      }
    };
    
    refetchPlan();
  }, [currentPlanType]);

  // Handler functions
  const handleSelectedRecipe = (selectedOption) => {
    const recipe = recipes.find(r => r.recipe_id === selectedOption.value);
    console.log("Selected recipe:", recipe);
    console.log("Selected option:", selectedOption);
    setSelectedRecipeData({
      recipe: recipe ? [recipe] : [],
      label: selectedOption.label,
      value: selectedOption.value,
      servings: recipe ? recipe.servings : selectedOption.servings, // Use recipe.servings from the found recipe
      dayValue: selectedRecipeData.dayValue,
      dayName: selectedRecipeData.dayName,
      courseValue: selectedRecipeData.courseValue,
      courseName: selectedRecipeData.courseName
    });
  };

  const handleSelectedMeal = (selectedOption) => {
    const meal = meals.find(m => m.id === selectedOption.value);
    setSelectedMealData({
      meal: meal ? [meal] : [],
      label: selectedOption.label,
      value: selectedOption.value,
      servings: "",
      dayValue: selectedMealData.dayValue,
      dayName: selectedMealData.dayName,
      courseValue: selectedMealData.courseValue,
      courseName: selectedMealData.courseName
    });
  };

  const handleNewMultiplier = (value) => {
    // Allow empty values while typing, but ensure we have a valid number for calculations
    if (value === "" || value === null || value === undefined) {
      setMultiplier("");
    } else {
      const numValue = parseFloat(value);
      setMultiplier(isNaN(numValue) ? "" : numValue);
    }
  };

  const handleNewMealMultiplier = (value) => {
    // Allow empty values while typing, but ensure we have a valid number for calculations
    if (value === "" || value === null || value === undefined) {
      setMealMultiplier("");
    } else {
      const numValue = parseFloat(value);
      setMealMultiplier(isNaN(numValue) ? "" : numValue);
    }
  };

  const handleRecipeDayChange = (selectedOption) => {
    setSelectedRecipeData(prev => ({
      ...prev,
      dayValue: selectedOption.value,
      dayName: selectedOption.label
    }));
  };

  const handleRecipeCourseChange = (selectedOption) => {
    setSelectedRecipeData(prev => ({
      ...prev,
      courseValue: selectedOption.value,
      courseName: selectedOption.label
    }));
  };

  const handleMealDayChange = (selectedOption) => {
    setSelectedMealData(prev => ({
      ...prev,
      dayValue: selectedOption.value,
      dayName: selectedOption.label
    }));
  };

  const handleMealCourseChange = (selectedOption) => {
    setSelectedMealData(prev => ({
      ...prev,
      courseValue: selectedOption.value,
      courseName: selectedOption.label
    }));
  };

  const handleEditMultiplier = (value) => {
    // Allow empty values while typing, but ensure we have a valid number for calculations
    if (value === "" || value === null || value === undefined) {
      setEditMultiplier("");
    } else {
      const numValue = parseFloat(value);
      setEditMultiplier(isNaN(numValue) ? "" : numValue);
    }
  };

  const handleEditRecipeChange = (selectedOption) => {
    const recipe = recipes.find(r => r.recipe_id === selectedOption.value);
    setEditRecipeData(prev => ({
      ...prev,
      recipe: recipe ? [recipe] : [],
      label: selectedOption.label,
      value: selectedOption.value,
      servings: selectedOption.servings
    }));
  };

  const handleEditDayChange = (selectedOption) => {
    setEditRecipeData(prev => ({
      ...prev,
      dayValue: selectedOption.value,
      dayName: selectedOption.label
    }));
  };

  const handleEditCourseChange = (selectedOption) => {
    setEditRecipeData(prev => ({
      ...prev,
      courseValue: selectedOption.value,
      courseName: selectedOption.label
    }));
  };

  const handleUpdateEntry = async () => {
    await updateWeeklyPlanRecipe();
    // Shopping list is now handled inside updateWeeklyPlanRecipe
  };

  const recipeOptions = useMemo(() => 
    filteredRecipesWithoutFree.map((recipe) => ({
      label: recipe.name,
      value: recipe.recipe_id,
      servings: recipe.servings,
    })), [filteredRecipesWithoutFree]
  );

  const filteredMeals = useMemo(() => 
    onlyUserMeals ? meals.filter(meal => meal.user_id !== 1) : meals,
    [meals, onlyUserMeals]
  );

  const mealOptions = useMemo(() => 
    filteredMeals.map((meal) => ({
      label: meal.meal_name,
      value: meal.id,
      notes: meal.meal_notes,
      tags: meal.meal_tags,
    })), [filteredMeals]
  );

  const fetchRecipeDetails = async (mealId) => {
    try {
      const details = await weeklyPlanAPI.fetchMealRecipes(mealId);
      return details.length > 0 ? details : null;
    } catch (error) {
      throw new Error("Failed to fetch recipe details");
    }
  };

  const handleTransitionToNextWeek = () => {
    setShowTransitionConfirm(true);
  };

  const confirmTransition = async () => {
    setIsTransitioning(true);
    try {
      const result = await weeklyPlanAPI.transitionToNextWeek();
      showSuccessToast(
        `Successfully transitioned to next week's plan! ${result.recipesTransitioned} recipes moved to current week.`
      );
      
      // Reset to current view and refresh data
      setCurrentPlanType('current');
      await fetchWeeklyPlan();
      
      // Refresh plan summary
      const summary = await weeklyPlanAPI.fetchPlanSummary();
      setPlanSummary(summary);
      setShowTransitionConfirm(false);
    } catch (error) {
      console.error("Error transitioning to next week:", error);
      showErrorToast(error.error || "Failed to transition to next week. Please try again.");
    } finally {
      setIsTransitioning(false);
    }
  };
  
  // Bulk operation handlers
  const handleCopyDay = (day) => {
    const dayRecipes = weeklyPlan.filter(r => r.day === day);
    if (dayRecipes.length === 0) {
      showWarningToast("No recipes to copy from this day");
      return;
    }
    
    setCopyDaySource(day);
    setShowCopyDayModal(true);
  };
  
  const confirmCopyDay = async (sourceDay, targetDay, replaceBehavior) => {
    setIsCopyingDay(true);
    const sourceRecipes = weeklyPlan.filter(r => r.day === sourceDay);
    let successCount = 0;
    
    try {
      // If replace behavior, clear target day first
      if (replaceBehavior === 'replace') {
        const targetRecipes = weeklyPlan.filter(r => r.day === targetDay);
        const deleteResults = await Promise.all(
          targetRecipes.map(async (recipe) => {
            const response = await weeklyPlanAPI.removeRecipeFromPlan({
              rowId: recipe.id,
              recipeId: recipe.recipe_id,
              recipeDay: recipe.day,
              recipeMeal: recipe.meal,
            });
            if (!response.ok) {
              console.error(`Failed to remove recipe ${recipe.id} from plan`);
            }
            return response;
          })
        );
        
        // Log results for debugging
        console.log(`Deleted ${deleteResults.filter(r => r.ok).length} recipes from ${targetDay}`);
        
        // Small delay to ensure database has processed all deletions
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Copy recipes to target day
      for (const recipe of sourceRecipes) {
        const recipeData = {
          recipe_id: recipe.recipe_id,
          meal_qty: recipe.meal_qty || 1,
          day: targetDay,
          meal: recipe.meal,
        };
        
        try {
          const response = await weeklyPlanAPI.addRecipe(recipeData, currentPlanType);
          if (response.ok) {
            successCount++;
            // Add to shopping list if it's current week
            if (currentPlanType === 'current') {
              await addToShoppingListWithDayMeal(recipe.recipe_id, targetDay, recipe.meal, recipe.meal_qty || 1);
            }
          }
        } catch (error) {
          console.error("Error copying recipe:", error);
        }
      }
      
      await fetchWeeklyPlan();
      
      if (nextWeekPlanningEnabled) {
        const summary = await weeklyPlanAPI.fetchPlanSummary();
        setPlanSummary(summary);
      }
      
      // Fetch prep items if we're on current week
      if (currentPlanType === 'current') {
        fetchPrepItems();
      }
      
      showSuccessToast(`Copied ${successCount} recipes from ${sourceDay} to ${targetDay}`);
      showSuccessAnimation();
      setShowCopyDayModal(false);
      setCopyDaySource(null);
    } catch (error) {
      console.error("Error copying day:", error);
      showErrorToast("Failed to copy day. Please try again.");
    } finally {
      setIsCopyingDay(false);
    }
  };
  
  const handleClearDay = (day) => {
    setClearDayTarget(day);
    setShowClearDayConfirm(true);
  };
  
  const confirmClearDay = async () => {
    if (!clearDayTarget) return;
    
    setIsClearingDay(true);
    const dayRecipes = weeklyPlan.filter(r => r.day === clearDayTarget);
    let successCount = 0;
    
    try {
      await Promise.all(
        dayRecipes.map(async (recipe) => {
          addAnimation(`recipe-${recipe.id}`, 'removing', 300);
          const response = await weeklyPlanAPI.removeRecipeFromPlan({
            rowId: recipe.id,
            recipeId: recipe.recipe_id,
            recipeDay: recipe.day,
            recipeMeal: recipe.meal,
          });
          if (response.ok) successCount++;
        })
      );
      
      await fetchWeeklyPlan();
      
      if (nextWeekPlanningEnabled) {
        const summary = await weeklyPlanAPI.fetchPlanSummary();
        setPlanSummary(summary);
      }
      
      // Fetch prep items if we're on current week
      if (currentPlanType === 'current') {
        fetchPrepItems();
      }
      
      showSuccessToast(`Cleared ${successCount} recipes from ${clearDayTarget}`);
      showSuccessAnimation();
      setShowClearDayConfirm(false);
      setClearDayTarget(null);
    } catch (error) {
      console.error("Error clearing day:", error);
      showErrorToast("Failed to clear day. Please try again.");
    } finally {
      setIsClearingDay(false);
    }
  };
  
  const handleClearMeal = (day, meal) => {
    setClearMealTarget({ day, meal });
    setShowClearMealConfirm(true);
  };
  
  const confirmClearMeal = async () => {
    if (!clearMealTarget.day || !clearMealTarget.meal) return;
    
    setIsClearingMeal(true);
    const mealRecipes = weeklyPlan.filter(
      r => r.day === clearMealTarget.day && r.meal === clearMealTarget.meal
    );
    let successCount = 0;
    
    try {
      await Promise.all(
        mealRecipes.map(async (recipe) => {
          addAnimation(`recipe-${recipe.id}`, 'removing', 300);
          const response = await weeklyPlanAPI.removeRecipeFromPlan({
            rowId: recipe.id,
            recipeId: recipe.recipe_id,
            recipeDay: recipe.day,
            recipeMeal: recipe.meal,
          });
          if (response.ok) successCount++;
        })
      );
      
      await fetchWeeklyPlan();
      
      if (nextWeekPlanningEnabled) {
        const summary = await weeklyPlanAPI.fetchPlanSummary();
        setPlanSummary(summary);
      }
      
      // Fetch prep items if we're on current week
      if (currentPlanType === 'current') {
        fetchPrepItems();
      }
      
      showSuccessToast(`Cleared ${successCount} ${clearMealTarget.meal} recipes from ${clearMealTarget.day}`);
      showSuccessAnimation();
      setShowClearMealConfirm(false);
      setClearMealTarget({ day: null, meal: null });
    } catch (error) {
      console.error("Error clearing meal:", error);
      showErrorToast("Failed to clear meal. Please try again.");
    } finally {
      setIsClearingMeal(false);
    }
  };

  const handleClearAllDays = () => {
    setShowClearAllDaysConfirm(true);
  };

  const confirmClearAllDays = async () => {
    setIsClearingAllDays(true);
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "None"];
    let totalSuccessCount = 0;
    let totalRecipeCount = 0;
    
    try {
      // Process each day
      for (const day of days) {
        const dayRecipes = weeklyPlan.filter(r => r.day === day);
        totalRecipeCount += dayRecipes.length;
        
        if (dayRecipes.length > 0) {
          const results = await Promise.all(
            dayRecipes.map(async (recipe) => {
              addAnimation(`recipe-${recipe.id}`, 'removing', 300);
              const response = await weeklyPlanAPI.removeRecipeFromPlan({
                rowId: recipe.id,
                recipeId: recipe.recipe_id,
                recipeDay: recipe.day,
                recipeMeal: recipe.meal,
              });
              return response.ok;
            })
          );
          
          totalSuccessCount += results.filter(success => success).length;
        }
      }
      
      await fetchWeeklyPlan();
      
      if (nextWeekPlanningEnabled) {
        const summary = await weeklyPlanAPI.fetchPlanSummary();
        setPlanSummary(summary);
      }
      
      // Fetch prep items if we're on current week
      if (currentPlanType === 'current') {
        fetchPrepItems();
      }
      
      showSuccessToast(`Cleared ${totalSuccessCount} recipes from all days`);
      showSuccessAnimation();
      setShowClearAllDaysConfirm(false);
    } catch (error) {
      console.error("Error clearing all days:", error);
      showErrorToast("Failed to clear all days. Please try again.");
    } finally {
      setIsClearingAllDays(false);
    }
  };
  
  const handleShowPrepList = () => {
    setShowPrepListModal(true);
  };
  

  if (!isLoggedIn) {
    return (
      <h3>
        Please{" "}
        <a
          className="btn btn-primary"
          href={`/signin?returnUrl=${encodeURIComponent(
            window.location.pathname
          )}`}
        >
          Sign In
        </a>{" "}
        to view
      </h3>
    );
  }

  return (
    <div className="weekly-plan-container">
      <WeeklyPlanToastContainer />
      <SuccessCheck show={showSuccess} />
      <div className="container">
        {/* Thaw Reminders Alert */}
        <ThawReminderAlert
          reminders={thawReminders}
          onDismiss={handleDismissThawReminder}
          onDismissAll={handleDismissAllThawReminders}
        />
      </div>
      <div className="container">
        {/* Compact Week Selector and Forms */}
        <div className="compact-weekly-container">
          {/* Compact header with week tabs */}
          {nextWeekPlanningEnabled && (
            <div className="compact-header">
              <div className="week-tabs">
                <button
                  className={`week-tab ${currentPlanType === 'current' ? 'active' : ''}`}
                  onClick={() => setCurrentPlanType('current')}
                >
                  Current Week <span className="count">({planSummary.currentWeek.recipeCount})</span>
                </button>
                <button
                  className={`week-tab ${currentPlanType === 'next' ? 'active' : ''}`}
                  onClick={() => setCurrentPlanType('next')}
                >
                  Next Week <span className="count">({planSummary.nextWeek.recipeCount})</span>
                </button>
              </div>
              {currentPlanType === 'next' && planSummary.nextWeek.recipeCount > 0 && (
                <LoadingButton
                  className="btn btn-sm btn-success activate-btn"
                  onClick={handleTransitionToNextWeek}
                  isLoading={isTransitioning}
                  loadingText="Activating..."
                >
                  <i className="fas fa-check me-1"></i>
                  Activate
                </LoadingButton>
              )}
            </div>
          )}
          
          {/* Forms Container - Directly attached */}
          <div className="forms-container">
            {/* Info message for next week - inside forms container */}
            {nextWeekPlanningEnabled && currentPlanType === 'next' && (
              <div className="planning-info">
                <i className="fas fa-info-circle"></i>
                <span>Planning Mode: Changes won't affect shopping list until activated</span>
              </div>
            )}
        <div className="row">
          {/* Add Recipe Form */}
          <div className="col-lg-6 form-divider pe-lg-3">
            <AddRecipeForm
              multiplier={multiplier}
              onMultiplierChange={handleNewMultiplier}
              recipeOptions={recipeOptions}
              selectedRecipeData={selectedRecipeData}
              onRecipeChange={handleSelectedRecipe}
              dayOptions={dayOptions}
              courseOptions={courseOptions}
              onDayChange={handleRecipeDayChange}
              onCourseChange={handleRecipeCourseChange}
              onAddRecipe={handleAddRecipe}
              showShoppingListConfirm={showShoppingListConfirm}
              onShoppingListConfirmChange={setShowShoppingListConfirm}
              calculateServings={calculateMealServings}
              onlyUserRecipes={onlyUserRecipes}
              onToggleUserRecipes={handleToggleUserRecipes}
              isLoading={isAddingRecipe}
              currentPlanType={currentPlanType}
            />
          </div>

          {/* Add Meal Form */}
          <div className="col-lg-6 ps-lg-3 mt-3 mt-lg-0">
            <AddMealForm
              mealMultiplier={mealMultiplier}
              onMealMultiplierChange={handleNewMealMultiplier}
              mealOptions={mealOptions}
              selectedMealData={selectedMealData}
              onMealChange={handleSelectedMeal}
              dayOptions={dayOptions}
              courseOptions={courseOptions}
              onDayChange={handleMealDayChange}
              onCourseChange={handleMealCourseChange}
              onAddMeal={handleAddMeal}
              showShoppingListConfirm={showShoppingListConfirm}
              onShoppingListConfirmChange={setShowShoppingListConfirm}
              onlyUserMeals={onlyUserMeals}
              onToggleUserMeals={handleToggleUserMeals}
              isLoading={isAddingMeal}
              currentPlanType={currentPlanType}
            />
          </div>
        </div>
      </div>
      </div>
      </div>

      {/* Weekly Plan Display */}
      {isLoadingPlan ? (
        <WeeklyPlanSkeleton />
      ) : (
        <WeeklyPlanDisplay
          weeklyPlan={weeklyPlan}
          dayTotals={dayTotals}
          onViewRecipe={handleViewRecipe}
          onEditRecipe={handleEditRecipe}
          onDeleteRecipe={handleDeleteFromDay}
          currentPlanType={currentPlanType}
          onCopyDay={handleCopyDay}
          onClearDay={handleClearDay}
          onClearMeal={handleClearMeal}
          onClearAllDays={handleClearAllDays}
          onShowPrepList={handleShowPrepList}
          hasPrepItems={hasPrepItems}
        />
      )}

      {/* Edit Recipe Modal */}
      <EditRecipeModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        editRecipeData={editRecipeData}
        editMultiplier={editMultiplier}
        onMultiplierChange={handleEditMultiplier}
        recipeOptions={recipeOptions}
        onRecipeChange={handleEditRecipeChange}
        dayOptions={dayOptions}
        courseOptions={courseOptions}
        onDayChange={handleEditDayChange}
        onCourseChange={handleEditCourseChange}
        onlyUserRecipes={onlyUserRecipes}
        onToggleUserRecipes={handleToggleUserRecipes}
        showEditShoppingListConfirm={showEditShoppingListConfirm}
        onShoppingListConfirmChange={setShowEditShoppingListConfirm}
        onSave={handleUpdateEntry}
        calculateServings={calculateMealServings}
        isUpdating={isUpdating}
        currentPlanType={currentPlanType}
      />

      {/* View Meal Modal */}
      <ViewMealModal
        show={showViewMealModal}
        onHide={() => setShowViewMealModal(false)}
        meal={viewMeal}
        mealRecipes={viewMealRecipe}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteConfirm}
        onHide={() => {
          setShowDeleteConfirm(false);
          setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
        itemName={deleteTarget?.name || ''}
        itemType="recipe"
        additionalWarning={currentPlanType === 'current' ? "This will also remove the ingredients from your shopping list." : null}
        isDeleting={isDeleting}
      />
      
      {/* Transition Confirmation Modal */}
      <TransitionConfirmationModal
        show={showTransitionConfirm}
        onHide={() => setShowTransitionConfirm(false)}
        onConfirm={confirmTransition}
        currentWeekCount={planSummary.currentWeek.recipeCount}
        nextWeekCount={planSummary.nextWeek.recipeCount}
        isTransitioning={isTransitioning}
      />
      
      {/* Clear Day Confirmation Modal */}
      <ClearDayConfirmationModal
        show={showClearDayConfirm}
        onHide={() => {
          setShowClearDayConfirm(false);
          setClearDayTarget(null);
        }}
        onConfirm={confirmClearDay}
        day={clearDayTarget}
        recipeCount={weeklyPlan.filter(r => r.day === clearDayTarget).length}
        isClearing={isClearingDay}
        currentPlanType={currentPlanType}
      />
      
      {/* Clear Meal Confirmation Modal */}
      <ClearMealConfirmationModal
        show={showClearMealConfirm}
        onHide={() => {
          setShowClearMealConfirm(false);
          setClearMealTarget({ day: null, meal: null });
        }}
        onConfirm={confirmClearMeal}
        day={clearMealTarget.day}
        meal={clearMealTarget.meal}
        recipeCount={weeklyPlan.filter(r => r.day === clearMealTarget.day && r.meal === clearMealTarget.meal).length}
        isClearing={isClearingMeal}
        currentPlanType={currentPlanType}
      />
      
      {/* Copy Day Modal */}
      <CopyDayModal
        show={showCopyDayModal}
        onHide={() => {
          setShowCopyDayModal(false);
          setCopyDaySource(null);
        }}
        onConfirm={confirmCopyDay}
        sourceDay={copyDaySource}
        recipeCount={weeklyPlan.filter(r => r.day === copyDaySource).length}
        isCopying={isCopyingDay}
        currentPlanType={currentPlanType}
      />
      
      {/* Clear All Days Confirmation Modal */}
      <ClearAllDaysConfirmationModal
        show={showClearAllDaysConfirm}
        onHide={() => setShowClearAllDaysConfirm(false)}
        onConfirm={confirmClearAllDays}
        totalRecipeCount={weeklyPlan.length}
        isClearing={isClearingAllDays}
        currentPlanType={currentPlanType}
      />
      
      {/* Weekly Prep List Modal */}
      <WeeklyPrepListModal
        show={showPrepListModal}
        onHide={() => setShowPrepListModal(false)}
        prepItems={prepItems}
        isMetric={isMetric}
      />
    </div>
  );
}

export default WeeklyPlan;